//@author: devin
import { NgTemplateOutlet, SlicePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, forwardRef, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { EventTooltipResponse, EventTooltipRow, eventTooltipRowType } from "../../../services/tooltip.service";
import { S25ItemComponent } from "../s25.item.component";
import { S25ItemTooltip } from "./s25.item.tooltip.directive";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";

@TypeManagerDecorator("s25-event-tooltip")
@Component({
    selector: "s25-event-tooltip",
    template: `
        @if (!loaded()) {
            Loading...
        } @else if (!item()) {
            Could not load event details
        } @else {
            <div class="boxHeader">
                <a
                    class="boxHeaderName"
                    s25-ng-router-link
                    [stateName]="'event'"
                    [stateParams]="{ itemId: item().event_id }"
                >
                    {{ item().event_name }}
                </a>
            </div>
            <div class="boxBody">
                @if (item().rows) {
                    @for (row of item().rows.data; track $index) {
                        <div>
                            <div>
                                @if (row.type !== tooltipRowType.EventTime || item().rsrv_start_dt) {
                                    <span class="c-dataLabel">{{ row.name }}</span>
                                }
                            </div>
                            <ng-container [ngTemplateOutlet]="rowTemplate" [ngTemplateOutletContext]="{ row }" />
                        </div>
                    }
                    @let spacesCount = item().spaces?.data?.length;
                    @for (loc of item().spaces?.data | slice: 0 : (spacesCount === 9 ? 9 : 8); track $index) {
                        <div class="dataItemSpaces">
                            @if ($index === 0) {
                                <span class="c-dataLabel">Location(s):</span>
                            }
                            <div class="ngItemSpace">
                                @if (!loc.is_hidden) {
                                    <span
                                        class="location ngInline s25-object s25-object-space summary"
                                        (click)="
                                            s25Item.itemDetail($event, {
                                                itemTypeId: Item.Ids.Location,
                                                itemId: loc.space_id,
                                            })
                                        "
                                        (contextmenu)="
                                            s25Item.openContextMenu($event, {
                                                itemTypeId: Item.Ids.Location,
                                                itemId: loc.space_id,
                                            })
                                        "
                                    >
                                        <div class="icon-space ngInline s25-object s25-object-space summary"></div>
                                        <div class="ngInline s25-object s25-object-space summary">
                                            {{ loc.space_name }}
                                        </div>
                                    </span>
                                } @else {
                                    <span class="location ngInline">
                                        <div class="icon-space ngInline"></div>
                                        <div class="ngInline">{{ loc.space_name }}</div>
                                    </span>
                                }
                            </div>
                        </div>
                    }
                    @if (spacesCount > 9) {
                        <div class="dataItemSpaces ngFinePrint">
                            <span class="location-placeholder"> ({{ spacesCount - 8 }} more locations) </span>
                        </div>
                    }
                }
            </div>
            <ng-template #rowTemplate let-rawRow="row">
                @let row = asRow(rawRow);
                @switch (row.type) {
                    @case (tooltipRowType.LabelledList) {
                        <ul class="labelledList">
                            @for (item of row.value; track $index) {
                                <li>
                                    <label>{{ item.label }}:</label>
                                    @if (item.nested) {
                                        <ng-container
                                            [ngTemplateOutlet]="rowTemplate"
                                            [ngTemplateOutletContext]="{ row: item.value }"
                                        />
                                    } @else {
                                        <span>{{ item.value }}</span>
                                    }
                                </li>
                            }
                        </ul>
                    }
                    @case (tooltipRowType.Bullets) {
                        <ul class="bullets">
                            @for (item of row.value; track $index) {
                                <li>
                                    @if (item.nested) {
                                        <ng-container
                                            [ngTemplateOutlet]="rowTemplate"
                                            [ngTemplateOutletContext]="{ row: item.value }"
                                        />
                                    } @else {
                                        {{ item.value }}
                                    }
                                </li>
                            }
                        </ul>
                    }
                    @case (tooltipRowType.Item) {
                        <s25-item
                            [itemTypeId]="row.value.itemTypeId"
                            [itemId]="row.value.itemId"
                            [itemName]="row.value.itemName"
                        />
                    }
                    @case (tooltipRowType.Raw) {
                        <span>{{ row.value }}</span>
                    }
                    @case (tooltipRowType.HTML) {
                        <span [innerHTML]="row.value"></span>
                    }
                    @case (tooltipRowType.Template) {
                        <ng-container
                            [ngTemplateOutlet]="row.value.template"
                            [ngTemplateOutletContext]="row.value.context"
                        />
                    }
                    @case (tooltipRowType.EventTime) {
                        <span>
                            @if (item().rsrv_start_dt) {
                                <span class="event-start">
                                    {{ item().rsrv_start_dt | dateFormat: s25Item.timeFormat() }}
                                    @if (
                                        (item().rsrv_start_dt | dateFormat: s25Item.dateFormat()) !==
                                        (item().rsrv_end_dt | dateFormat: s25Item.dateFormat())
                                    ) {
                                        <span class="meridiem angular fulldate">
                                            {{ " (" + (item().rsrv_start_dt | dateFormat: s25Item.dateFormat()) + ")" }}
                                        </span>
                                    }
                                </span>
                                <span class="meridiem">{{ " - " }}</span>
                                <span class="event-end">
                                    {{ item().rsrv_end_dt | dateFormat: s25Item.timeFormat() }}
                                    @if (
                                        (item().rsrv_start_dt | dateFormat: s25Item.dateFormat()) !==
                                        (item().rsrv_end_dt | dateFormat: s25Item.dateFormat())
                                    ) {
                                        <span class="meridiem angular fulldate">
                                            {{ " (" + (item().rsrv_end_dt | dateFormat: s25Item.dateFormat()) + ")" }}
                                        </span>
                                    }
                                </span>
                            }
                        </span>
                    }
                }
            </ng-template>
        }
    `,
    host: {
        "[class.eventBox]": "loaded()",
    },
    styles: `
        .location,
        .location-placeholder,
        ul,
        ol {
            padding: 0.25rem 1rem;
        }
        .event-start,
        .fulldate,
        .event-end {
            /* override padding from qtip-overwrites "#s25 .qtip-content .eventBox span" */
            padding: 0 !important;
        }
        .meridiem {
            /* override padding from qtip-overwrites "#s25 .qtip-content .eventBox span" */
            padding: 0 0.25rem !important;
            /* override default meridiem left margin of 1px */
            margin-left: 0;
        }
        ul,
        ol {
            padding-top: 0;
            font-size: 0.9em;
            margin: 0;
            list-style-position: inside;
            span {
                padding: 0 !important;
            }
            > li > * {
                display: inline-block;
            }
        }
        .labelledList {
            list-style-type: none;

            label {
                font-style: italic;
                font-weight: 400;
                min-width: 6em;
            }
        }

        :host .bullets s25-item ::ng-deep .s25-item-holder {
            vertical-align: middle;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgTemplateOutlet, S25PipesModule, forwardRef(() => S25ItemComponent), SlicePipe, S25RouterLinkDirective],
})
export class S25EventTooltip extends S25ItemTooltip<EventTooltipResponse> {
    protected readonly tooltipRowType = eventTooltipRowType;

    protected asRow(rawRow: any) {
        return rawRow as EventTooltipRow;
    }
}
